import { useEffect, useState } from "react";
import { toast } from "sonner";
import { FileText, Loader2, Music2 } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { listSongs } from "@/lib/creatorApi";
import { getProject, listProjects } from "@/lib/studioApi";
import { safeApiError } from "@/lib/safeErrorText";

const row = "flex w-full items-center gap-2 rounded-xl border border-white/10 bg-white/[0.03] px-3 py-2 text-left text-[13px] transition-colors duration-150 hover:bg-white/[0.08]";

/** Pick lyrics from a song project or My Songs, or type / paste them. */
export default function LyricsSource({ open, onClose, lyrics, instrumental, onSave }) {
  const [text, setText] = useState(lyrics?.text || "");
  const [source, setSource] = useState(lyrics?.source || null);
  const [list, setList] = useState(null);
  const [busy, setBusy] = useState(null);
  useEffect(() => { if (open) { setText(lyrics?.text || ""); setSource(lyrics?.source || null); } }, [open, lyrics]);
  useEffect(() => {
    if (!open || list) return;
    Promise.all([listProjects({ limit: 60 }).catch(() => []), listSongs({ limit: 100 }).catch(() => [])]).then(([ps, ss]) => {
      const rows = [...ps.map((p) => ({ key: `p:${p.id}`, kind: "Project", title: p.title || "Untitled song", project: p.id, match: p.id === instrumental?.project_id })),
        ...ss.filter((s) => s.lyrics?.trim()).map((s) => ({ key: `s:${s.id}`, kind: "Song", title: s.title, text: s.lyrics, match: s.id === instrumental?.song_id }))];
      setList(rows.sort((a, b) => Number(b.match) - Number(a.match)));
    });
  }, [open, list, instrumental]);
  const pick = async (r) => {
    setBusy(r.key);
    try {
      const t = r.text ?? (await getProject(r.project)).lyrics;
      if (!t?.trim()) { toast.message("That project has no lyrics yet"); return; }
      setText(t);
      setSource(r.title);
    } catch (e) { toast.error("Couldn't load those lyrics", { description: safeApiError(e) }); }
    finally { setBusy(null); }
  };
  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent data-testid="lyrics-source" className="max-w-3xl border-white/10 bg-[#0b0e1c] text-white">
        <DialogHeader><DialogTitle className="flex items-center gap-2 font-display"><FileText className="h-4 w-4 text-amber-200" /> Lyrics for the prompter</DialogTitle></DialogHeader>
        <div className="grid gap-4 md:grid-cols-[240px_minmax(0,1fr)]">
          <div className="max-h-[50vh] space-y-1.5 overflow-y-auto">
            <p className="font-mono text-[10px] uppercase tracking-[0.16em] text-white/45">Use lyrics from</p>
            {list === null ? <Loader2 className="h-4 w-4 animate-spin text-white/50" /> : null}
            {(list || []).map((r) => (
              <button key={r.key} type="button" data-testid={`lyrics-from-${r.key.replace(":", "-")}`} onClick={() => pick(r)} className={row}>
                {busy === r.key ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Music2 className="h-3.5 w-3.5 text-amber-200" />}
                <span className="min-w-0 flex-1 truncate">{r.title}</span>
                <span className="font-mono text-[9px] uppercase text-white/40">{r.match ? "Your music" : r.kind}</span>
              </button>
            ))}
            {list?.length === 0 ? <p className="text-[12px] text-white/50">No songs with lyrics yet — type them on the right.</p> : null}
          </div>
          <textarea data-testid="lyrics-text" value={text} onChange={(e) => { setText(e.target.value); }} rows={14} maxLength={20000}
            placeholder={"[Verse 1]\nType or paste your lyrics here,\none sung line per row…"} className="w-full rounded-2xl border border-white/10 bg-black/40 p-3 font-display text-[14px] leading-relaxed text-white focus:border-amber-300/50 focus:outline-none" />
        </div>
        <div className="flex justify-end gap-2">
          <button type="button" data-testid="lyrics-cancel" onClick={onClose} className="rounded-full border border-white/15 px-4 py-2 text-[13px] text-white/75">Cancel</button>
          <button type="button" data-testid="lyrics-save" onClick={() => onSave({ text, source: text.trim() ? source : null })} className="rounded-full bg-amber-300 px-5 py-2 text-[13px] font-semibold text-[#05070f]">Use these lyrics</button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
